import { Button, ClickEvent, DataSourceMode, DataSourceRemoteDataChangeEvent, ModelDataChangeType, TableRowDisplayEvent, DataSourceExecutionEvent, Tab, Tabset, Panel, TableSelectionEvent, TableRow, ContextMenuLabelProps, TableContentsChangedEvent } from "@mcleod/components";
import { AutogenLayoutTableRapidAlerts } from "./autogen/AutogenLayoutTableRapidAlerts";
import { StringUtil, UserSettings, Api, Navigation, UrlUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";

export class TableRapidAlerts extends AutogenLayoutTableRapidAlerts {
    private _tab: Tab;
    private _alertType: string;
    private _alertDescription: string;
    private onSelectionChangeCallback: (hasSelectedRows: boolean) => void;
    
    // Optional callback to get all selected rows across all tables
    public getAllSelectedRows?: () => TableRow[];

    get tab(): Tab {
        return this._tab;
    }

    set tab(value: Tab) {
        this._tab = value;
    }

    set alertType(value: string) {
        this._alertType = value;
    }

    get alertType(): string {
        return this._alertType;
    }

    set alertDescription(value: string) {
        this._alertDescription = value;
    }

    get alertDescription(): string {
        return this._alertDescription;
    }

    override onLoad(): void {
        this.tableRapidAlerts.addSelectionListener(this.handleRowSelection);
        this.tableRapidAlerts.addFilterChangedListener(this.handleFilterChange);
    }

    public setOnSelectionChangeCallback(callback: (hasSelectedRows: boolean) => void): void {
        this.onSelectionChangeCallback = callback;
    }

    private handleRowSelection = (event: TableSelectionEvent): void => {
        const hasSelectedRows = this.tableRapidAlerts.selectedRows.length > 0;
        if (this.onSelectionChangeCallback) {
            this.onSelectionChangeCallback(hasSelectedRows);
        }
    }

    private handleFilterChange = (): void => {
        this.updateTabCaption();
    }

    sourceRapidAlertAfterExecution(event: DataSourceExecutionEvent) {
        this.applyFilter();
    }

    sourceRapidAlertOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        let changeType = event.change.type;
        const changeData = event.change.data;

        if (changeData.get("is_confirmed") || UserSettings.getUserId() !== changeData.get("user_id")) {
            changeType = ModelDataChangeType.DELETE;
        }

        this.sourceRapidAlert.handleDataChange({
            type: changeType,
            data: changeData,
            lastUpdateNanos: event.change.lastUpdateNanos
        });
        this.applyFilter();
        event.preventDefault();
    }

    public applyFilter(filterText?: string) {
        this.tableRapidAlerts.customFilter = (tableRow) => {
            return tableRow.data.get("alert_id") === this.alertType;
        };
        if (filterText !== undefined) {
            this.tableRapidAlerts.filterValue = filterText;
        }
    }

    private updateTabCaption() {
        const unreadCount = this.tableRapidAlerts.filteredRows.filter(item => (this.alertType === item.data.get("alert_id") && !item.data.get("is_read"))).length;
        this.tab.heading.fontBold = unreadCount > 0;
        this.tab.heading.fontSize = unreadCount == 0 ? "medium" : "large";
        this.tab.caption = `${this.alertDescription} (${unreadCount})`;
    }

    tableRapidAlertsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const isRead = tableRow.data.get("is_read");
        tableRow.cells.forEach(cell => {
            if (!isRead) {
                cell.fontBold = true;
                cell.fontSize = "large";
            }
            else {
                cell.fontBold = false;
                cell.fontSize = "medium";
            }
        });
        // Add context menu for right-click
        tableRow.contextMenuItems = () => this.generateContextMenuItems(tableRow);

        // Add double-click listener to navigate to detail_url if present, otherwise show dialog with message
        tableRow.addDblClickListener(async () => {
            // Mark as read if not already
            if (!tableRow.data.get("is_read")) {
                await Api.update("lme/dispatch/rapid-alert-msg", {
                    ids: [tableRow.data.get("id")],
                    read: true
                });
            }
            const detailUrl = tableRow.data.get("detail_url");
            if (detailUrl) {
                Navigation.navigateTo(detailUrl, { newTab: true });
            } else {
                const message = tableRow.data.get("message") || "No detail available for this alert.";
                CommonDialogs.showDialog(message);
            }
        });
    }

    private generateContextMenuItems(tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        // If right-clicked row is not selected, deselect all rows in all accordions and select only this row
        let selectedRows: TableRow[];
        if (this.getAllSelectedRows) {
            selectedRows = this.getAllSelectedRows();
            if (!selectedRows.includes(tableRow)) {
                // Deselect all rows in all accordions/tables
                selectedRows.forEach(row => {
                    if (row.table.selectedRows.length > 0) {
                        row.table.clearSelectedRows();
                    }
                });
                // Select only the right-clicked row
                tableRow.table.selectedRow = tableRow;
                selectedRows = [tableRow];
            }
        } else {
            selectedRows = tableRow.table.selectedRows.length > 0
                ? tableRow.table.selectedRows
                : [tableRow];
            if (!selectedRows.includes(tableRow)) {
                tableRow.table.clearSelectedRows();
                tableRow.table.selectedRow = tableRow;
                selectedRows = [tableRow];
            }
        }

        const menuItems: Partial<ContextMenuLabelProps>[] = [];

        // Only add "Mark Read" if at least one selected row is not read
        const unreadIds = selectedRows.filter(row => !row.data.get("is_read")).map(row => row.data.get("id"));
        if (unreadIds.length > 0) {
            menuItems.push({
                text: "Mark Read",
                onClick: async () => {
                    if (unreadIds.length > 0) {
                        await Api.update("lme/dispatch/rapid-alert-msg", {
                            ids: unreadIds,
                            read: true
                        });
                    }
                }
            });
        }

        // Mark Confirmed for all selected rows
        const unconfirmedIds = selectedRows.filter(row => !row.data.get("is_confirmed")).map(row => row.data.get("id"));
        if (unconfirmedIds.length > 0) {
            menuItems.push({
                text: "Mark Confirmed",
                onClick: async () => {
                    if (unconfirmedIds.length > 0) {
                        await Api.update("lme/dispatch/rapid-alert-msg", {
                            ids: unconfirmedIds,
                            read: true,
                            confirm: true
                        });
                    }
                }
            });
        }

        // Add "Open {detail_type}" action if detail_url is present and only one row is selected
        if (selectedRows.length === 1) {
            const row = selectedRows[0];
            const detailUrl = row.data.get("detail_url");
            const detailType = row.data.get("detail_type");
            if (detailUrl && detailType) {
                menuItems.push({
                    text: `Open ${detailType}`,
                    onClick: () => {
                        Navigation.navigateTo(detailUrl, { newTab: true });
                    }
                });
            }
            // Add "Open {detail_type2}" action if detail_url2 is present
            const detailUrl2 = row.data.get("detail_url2");
            const detailType2 = row.data.get("detail_type2");
            if (detailUrl2 && detailType2) {
                menuItems.push({
                    text: `Open ${detailType2}`,
                    onClick: () => {
                        Navigation.navigateTo(detailUrl2, { newTab: true });
                    }
                });
            }
        }

        return menuItems;
    }
}